<div class="bg-white rounded-xl border border-slate-100 shadow-sm p-5 mb-4">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3 mb-4">
        <div class="flex items-center gap-3 min-w-0">
            <div class="bg-blue-50 text-blue-600 p-2 rounded-lg flex">
                <mat-icon>location_on</mat-icon>
            </div>
            <h3 class="text-base font-semibold text-slate-900 truncate m-0">{{ detectionPoint.name }}</h3>
        </div>
        @if (!editing()) {
        <div class="flex items-center shrink-0">
            <button mat-icon-button (click)="startEdit()" [disabled]="saving" title="Edit">
                <mat-icon class="text-slate-500">edit</mat-icon>
            </button>
            <button mat-icon-button (click)="onDelete()" [disabled]="saving" title="Delete">
                <mat-icon class="text-red-500">delete</mat-icon>
            </button>
        </div>
        }
    </div>

    @if (!editing()) {
    <!-- Summary -->
    <dl class="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-3 items-center text-sm m-0">
        <dt class="text-slate-500">Camera</dt>
        <dd class="m-0">
            @if (detectionPoint.camera?.id) {
            <button type="button" (click)="onNavigateToCamera()"
                class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium">
                <mat-icon class="camera-icon">videocam</mat-icon>
                {{ detectionPoint.camera?.deviceName }}
                <mat-icon class="camera-icon">open_in_new</mat-icon>
            </button>
            } @else {
            <span class="text-slate-400 italic">No camera</span>
            }
        </dd>

        <dt class="text-slate-500">Direction</dt>
        <dd class="m-0">
            <span class="inline-block bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded">
                {{ getDirectionLabel(detectionPoint.expectedDirections) }}
            </span>
        </dd>

        <dt class="text-slate-500">Status</dt>
        <dd class="m-0">
            @if (detectionPoint.isActive) {
            <span class="inline-block bg-emerald-50 text-emerald-700 text-xs font-medium px-2 py-1 rounded">Active</span>
            } @else {
            <span class="inline-block bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded">Inactive</span>
            }
        </dd>

        <dt class="text-slate-500">Reversible lane</dt>
        <dd class="m-0">
            @if (laneWindows; as lane) {
            <ng-container [ngTemplateOutlet]="laneOverview" [ngTemplateOutletContext]="{ $implicit: lane }"></ng-container>
            } @else {
            <span class="text-slate-400 italic">Off</span>
            }
        </dd>

        @if (detectionPoint.description) {
        <dt class="text-slate-500 self-start">Description</dt>
        <dd class="m-0 text-slate-700">{{ detectionPoint.description }}</dd>
        }
    </dl>
    } @else {
    <!-- Edit -->
    <div class="space-y-4">
        <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Name</label>
            <input type="text" [ngModel]="name()" (ngModelChange)="name.set($event)"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            @if (name().trim().length < 2) {
            <p class="text-xs text-red-600 mt-1">At least 2 characters</p>
            }
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
                <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Camera</label>
                <select [ngModel]="cameraId()" (ngModelChange)="cameraId.set($event)"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100">
                    @for (camera of cameraOptions; track camera.id) {
                    <option [value]="camera.id">{{ camera.name }}</option>
                    }
                </select>
            </div>
            <div>
                <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Direction</label>
                <select [ngModel]="expectedDirections()" (ngModelChange)="expectedDirections.set($event)"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100">
                    @for (direction of directions; track direction) {
                    <option [value]="direction">{{ getDirectionLabel(direction) }}</option>
                    }
                </select>
            </div>
        </div>

        <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Description</label>
            <textarea rows="2" [ngModel]="description()" (ngModelChange)="description.set($event)"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"></textarea>
        </div>

        <div class="flex items-center justify-between gap-4">
            <span class="text-sm font-medium text-slate-900">Active</span>
            <label class="relative inline-flex items-center cursor-pointer shrink-0">
                <input type="checkbox" class="sr-only peer" [checked]="isActive()"
                    (change)="isActive.set($any($event.target).checked)" />
                <div class="toggle-track"></div>
            </label>
        </div>

        @if (formCanBeReversible()) {
        <div class="rounded-xl border border-slate-200 p-4 space-y-4">
            <div class="flex items-center justify-between gap-4">
                <span class="text-sm font-medium text-slate-900">Reversible lane</span>
                <label class="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" class="sr-only peer" [checked]="isReversible()"
                        (change)="isReversible.set($any($event.target).checked)" />
                    <div class="toggle-track"></div>
                </label>
            </div>

            <!-- The server copies the schedule (or its removal) to the lane's other points. -->
            @if (isReversible() || hasLaneSchedule) {
            <p class="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 m-0">
                <mat-icon class="hint-icon">info</mat-icon>
                This barrier has one schedule. Saving it here also changes it for the other camera on this barrier.
            </p>
            }

            @if (isReversible()) {
            <div class="grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Entry from</label>
                    <input type="time" [ngModel]="laneEntryFrom()" (ngModelChange)="laneEntryFrom.set($event)"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Exit from</label>
                    <input type="time" [ngModel]="laneExitFrom()" (ngModelChange)="laneExitFrom.set($event)"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div>
                    <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Handover (min)</label>
                    <input type="number" [min]="minHandover" [ngModel]="laneHandoverMinutes()"
                        (ngModelChange)="laneHandoverMinutes.set(+$event)"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </div>
            </div>
            @if (laneEntryFrom() && laneEntryFrom() === laneExitFrom()) {
            <p class="text-xs text-red-600 m-0">Entry and exit times must differ</p>
            } @else if (laneHandoverMinutes() < minHandover) {
            <p class="text-xs text-red-600 m-0">At least {{ minHandover }} minutes</p>
            } @else if (laneWindowsPreview(); as lane) {
            <ng-container [ngTemplateOutlet]="laneOverview" [ngTemplateOutletContext]="{ $implicit: lane }"></ng-container>
            } @else {
            <p class="text-xs text-red-600 m-0">Handover is longer than an open period</p>
            }
            }
        </div>
        }

        <div class="flex justify-end gap-2 pt-2">
            <button type="button" (click)="cancelEdit()" [disabled]="saving"
                class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancel
            </button>
            <button type="button" (click)="onSave()" [disabled]="saving || !formValid()"
                class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Save
            </button>
        </div>
    </div>
    }
</div>

<!-- A lane schedule as it runs: a 24-hour bar and each side's open window. -->
<ng-template #laneOverview let-lane>
    <div class="flex flex-col gap-2 w-full max-w-md">
        <div>
            <div class="relative h-3 rounded-full overflow-hidden bg-slate-100">
                @for (segment of lane.segments; track $index) {
                <div class="absolute inset-y-0 segment" [ngClass]="'segment-' + segment.kind"
                    [style.left.%]="segment.left" [style.width.%]="segment.width"></div>
                }
            </div>
            <div class="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
                @for (tick of barTicks; track $index) {
                <span>{{ tick }}</span>
                }
            </div>
        </div>
        <div class="flex items-center gap-2 text-sm">
            <span class="legend-dot segment-entry"></span>
            <span class="w-14 text-xs font-semibold text-slate-500 uppercase">Entry</span>
            <span class="font-mono text-slate-900">{{ lane.entry }}</span>
        </div>
        <div class="flex items-center gap-2 text-sm">
            <span class="legend-dot segment-exit"></span>
            <span class="w-14 text-xs font-semibold text-slate-500 uppercase">Exit</span>
            <span class="font-mono text-slate-900">{{ lane.exit }}</span>
        </div>
        <div class="flex items-center gap-2 text-sm">
            <span class="legend-dot segment-closed"></span>
            <span class="w-14 text-xs font-semibold text-slate-500 uppercase">Closed</span>
            <span class="text-slate-900">{{ lane.handover }} min before each switch</span>
        </div>
    </div>
</ng-template>
